import type {
  Connection,
  ConnectionCreate,
  ConnectionContact,
  MatterInsurance,
  SourceOwnedConnectionCreate,
} from "@/lib/types";
import { formatContactsName } from "@/lib/utils/format";
import { getConnectionPreviewContact } from "./item-contact-connections";

export const INSURANCE_CONTACT_CONNECTION_TYPES = {
  "Adjuster Supervisor": "insurance_claim_to_adjuster_supervisor",
  "Bodily Injury Adjuster": "insurance_claim_to_bodily_injury_adjuster",
  Insured: "insurance_claim_to_insured",
  Insurer: "insurance_claim_to_insurer",
  "MedPay Adjuster": "insurance_claim_to_medpay_adjuster",
  "Policy Holder": "insurance_claim_to_policy_holder",
  "Property Damage Adjuster": "insurance_claim_to_property_damage_adjuster",
  "Subrogation Representative": "insurance_claim_to_subrogation_representative",
  "Subrogation Vendor": "insurance_claim_to_subrogation_vendor",
} as const;

export type SupportedInsuranceContactRole =
  keyof typeof INSURANCE_CONTACT_CONNECTION_TYPES;

const getInsuranceConnectionTypes = (role: SupportedInsuranceContactRole) => {
  const nativeType = INSURANCE_CONTACT_CONNECTION_TYPES[role];
  return [
    nativeType,
    nativeType.replace("insurance_claim_to_", "insurance_claim_to_legacy_"),
  ];
};

const isActiveInsuranceContactConnection = (
  connection: Connection,
  insuranceId?: string,
) =>
  connection.sourceObjectType === "insurance_claim" &&
  (!insuranceId || connection.sourceObjectId === insuranceId) &&
  connection.targetObjectType === "contact" &&
  connection.state === "active";

export const getInsuranceContactConnections = (
  insurance: MatterInsurance | undefined,
  role: SupportedInsuranceContactRole,
): Connection[] => {
  if (!insurance) return [];

  return insurance.connections.filter(
    (connection) =>
      getInsuranceConnectionTypes(role).includes(connection.connectionType) &&
      isActiveInsuranceContactConnection(connection, insurance.id),
  );
};

export const getInsuranceContactConnection = (
  insurance: MatterInsurance | undefined,
  role: SupportedInsuranceContactRole,
): Connection | undefined => getInsuranceContactConnections(insurance, role)[0];

export const getDesiredInsuranceContactIdsForSingleSlot = (
  insurance: MatterInsurance,
  role: SupportedInsuranceContactRole,
  selectedContactId?: string,
): string[] => [
  ...(selectedContactId ? [selectedContactId] : []),
  ...getInsuranceContactConnections(insurance, role)
    .slice(
      ["Insurer", "MedPay Adjuster", "Property Damage Adjuster"].includes(role)
        ? Infinity
        : 1,
    )
    .map((connection) => connection.targetObjectId)
    .filter((contactId) => contactId !== selectedContactId),
];

export const getInsuranceConnectionContactName = (
  contact: ConnectionContact | null | undefined,
) => (contact ? formatContactsName(contact) : undefined) ?? "Unknown contact";

export const getInsuranceConnectionFormContact = (connection?: Connection) => {
  if (!connection?.contact) return undefined;

  return {
    contactName: getInsuranceConnectionContactName(connection.contact),
    id: connection.contact.id,
    type: connection.contact.type,
  };
};

export const getInsuranceConnectionPreviewContact = getConnectionPreviewContact;

export const createInsuranceContactConnectionInput = (
  insuranceId: string,
  contactId: string,
  role: SupportedInsuranceContactRole,
): ConnectionCreate => ({
  connectionType: INSURANCE_CONTACT_CONNECTION_TYPES[role],
  operationId: crypto.randomUUID(),
  sourceObjectId: insuranceId,
  sourceObjectType: "insurance_claim",
  targetObjectId: contactId,
  targetObjectType: "contact",
});

export const createInsuranceContactConnectionChildInput = (
  contactId: string,
  role: SupportedInsuranceContactRole,
): SourceOwnedConnectionCreate => ({
  connectionType: INSURANCE_CONTACT_CONNECTION_TYPES[role],
  targetObjectId: contactId,
  targetObjectType: "contact",
});

export const isInsuranceContactConnection = (connection: Connection): boolean =>
  isActiveInsuranceContactConnection(connection);
